import React, { useState } from 'react';
import { Box, Container, Heading, Text, SimpleGrid, VStack, useColorModeValue, Divider, Image, Badge, HStack, Button, Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton, useDisclosure, List, ListItem, ListIcon, Flex, ButtonGroup, Tag } from '@chakra-ui/react';
import { ExternalLink, Github, Check, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
const MotionBox = motion(Box);
const ProjectModal = ({
  project,
  isOpen,
  onClose
}) => {
  const modalBg = useColorModeValue('white', 'gray.800');
  return <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered data-id="element-207">
      <ModalOverlay backdropFilter="blur(10px)" data-id="element-208" />
      <ModalContent bg={modalBg} borderRadius="lg" data-id="element-209">
        <ModalHeader data-id="element-210">{project.title}</ModalHeader>
        <ModalCloseButton data-id="element-211" />
        <ModalBody data-id="element-212">
          <Image src={project.image} alt={project.title} width="100%" height="250px" objectFit="cover" borderRadius="md" mb={4} data-id="element-213" />
          <HStack spacing={2} mb={4} flexWrap="wrap" data-id="element-214">
            {project.tags.map((tag, index) => <Badge key={index} colorScheme="blue" fontSize="xs" data-id="element-215">
                {tag}
              </Badge>)}
          </HStack>
          <Text mb={4} data-id="element-216">{project.description}</Text>
          <Heading as="h4" size="sm" mb={2} data-id="element-217">
            Technologies Used
          </Heading>
          <HStack spacing={2} mb={4} flexWrap="wrap" data-id="element-218">
            {project.technologies.map((tech, index) => <Tag key={index} size="md" variant="subtle" colorScheme="blue" mb={2} data-id="element-219">
                {tech}
              </Tag>)}
          </HStack>
          <Heading as="h4" size="sm" mb={2} data-id="element-220">
            Challenges & Solutions
          </Heading>
          <List spacing={2} mb={4} data-id="element-221">
            {project.challenges.map((challenge, index) => <ListItem key={index} display="flex" data-id="element-222">
                <ListIcon as={Check} color="blue.500" mt={1} data-id="element-223" />
                <Text data-id="element-224">{challenge}</Text>
              </ListItem>)}
          </List>
          {project.results && <>
              <Heading as="h4" size="sm" mb={2} data-id="element-225">
                Results
              </Heading>
              <Text mb={4} data-id="element-226">{project.results}</Text>
            </>}
        </ModalBody>
        <ModalFooter data-id="element-227">
          <ButtonGroup spacing={4} data-id="element-228">
            {project.demoLink && <Button as="a" href={project.demoLink} leftIcon={<ExternalLink size={16} data-id="element-230" />} colorScheme="blue" target="_blank" rel="noopener noreferrer" data-id="element-229">
                Live Demo
              </Button>}
            {project.codeLink && <Button as="a" href={project.codeLink} leftIcon={<Github size={16} data-id="element-232" />} variant="outline" target="_blank" rel="noopener noreferrer" data-id="element-231">
                View Code
              </Button>}
            <Button onClick={onClose} variant="ghost" data-id="element-233">
              Close
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>;
};
const ProjectCard = ({
  project,
  onClick
}) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  return <MotionBox borderRadius="lg" overflow="hidden" bg={cardBg} boxShadow="md" border="1px" borderColor={borderColor} transition={{
    duration: 0.3
  }} whileHover={{
    y: -5,
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
  }} onClick={onClick} cursor="pointer" initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} data-id="element-234">
      <Image src={project.image} alt={project.title} width="100%" height="200px" objectFit="cover" data-id="element-235" />
      <Box p={6} data-id="element-236">
        <Heading as="h3" size="md" mb={2} data-id="element-237">
          {project.title}
        </Heading>
        <HStack spacing={2} mb={3} flexWrap="wrap" data-id="element-238">
          {project.tags.map((tag, index) => <Badge key={index} colorScheme="blue" fontSize="xs" data-id="element-239">
              {tag}
            </Badge>)}
        </HStack>
        <Text color={useColorModeValue('gray.600', 'gray.300')} mb={4} noOfLines={3} data-id="element-240">
          {project.description}
        </Text>
        <Button size="sm" rightIcon={<ArrowRight size={16} data-id="element-242" />} variant="link" colorScheme="blue" data-id="element-241">
          View Details
        </Button>
      </Box>
    </MotionBox>;
};
export function Projects() {
  const bgColor = useColorModeValue('white', 'gray.900');
  const [filter, setFilter] = useState('All');
  const [selectedProject, setSelectedProject] = useState(null);
  const {
    isOpen,
    onOpen,
    onClose
  } = useDisclosure();
  const projects = [{
    title: 'Secure Payment Gateway',
    description: 'Developed a secure payment processing system with end-to-end encryption, fraud detection, and compliance with PCI DSS standards for a major financial institution.',
    image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    tags: ['Security', 'Backend'],
    category: 'Security',
    technologies: ['Django', 'PostgreSQL', 'Redis', 'AWS KMS', 'Docker'],
    challenges: ['Implemented advanced encryption for sensitive financial data', 'Designed real-time fraud detection using machine learning algorithms', 'Ensured compliance with international security standards', 'Built comprehensive audit logging and monitoring systems'],
    results: 'Successfully processed over $10M in transactions with zero security incidents, reducing fraud by 45% compared to previous system.',
    demoLink: '#',
    codeLink: '#'
  }, {
    title: 'Vulnerability Assessment Platform',
    description: 'Created an automated vulnerability scanning and reporting platform that identifies security weaknesses in web applications and provides remediation recommendations.',
    image: 'https://images.unsplash.com/photo-1510511459019-5dda7724fd87?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    tags: ['Security', 'OWASP', 'Python'],
    category: 'Security',
    technologies: ['Python', 'Flask', 'MongoDB', 'Docker', 'OWASP ZAP API'],
    challenges: ['Integrated with multiple security scanning tools (OWASP ZAP, Nessus)', 'Developed custom vulnerability detection plugins', 'Created a risk scoring algorithm to prioritize findings', 'Built an intuitive dashboard for security teams'],
    results: 'Platform has identified over 200 critical vulnerabilities across client applications, preventing potential data breaches.',
    demoLink: '#',
    codeLink: '#'
  }, {
    title: 'Microservices Architecture',
    description: 'Designed and implemented a scalable microservices architecture for a high-traffic e-commerce platform, focusing on resilience and security.',
    image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    tags: ['Backend', 'Microservices', 'DevOps'],
    category: 'Backend',
    technologies: ['Node.js', 'Express', 'Kubernetes', 'RabbitMQ', 'MongoDB', 'Redis'],
    challenges: ['Decomposed monolithic application into 12 microservices', 'Implemented service discovery and circuit breaking patterns', 'Designed secure inter-service communication with JWT', 'Created CI/CD pipelines for automated testing and deployment'],
    results: "Improved system uptime to 99.99%, reduced deployment time by 80%, and increased the platform's ability to handle concurrent users by 300%.",
    demoLink: '#',
    codeLink: '#'
  }, {
    title: 'STEM Robotics Platform',
    description: 'Developed an educational platform for teaching robotics and programming in underserved schools across Ghana, including curriculum and competition frameworks.',
    image: 'https://images.unsplash.com/photo-1535378620166-273708d44e4c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    tags: ['Education', 'Robotics', 'Full Stack'],
    category: 'Full Stack',
    technologies: ['React', 'Django', 'PostgreSQL', 'WebSockets', 'Arduino'],
    challenges: ['Created interactive learning modules for students with varying technical backgrounds', 'Developed remote programming capabilities for robotics hardware', 'Built a judging system for robotics competitions', 'Designed for use in low-bandwidth environments'],
    results: 'Platform has been adopted by 25+ schools, reaching over 1,000 students and significantly increasing interest in STEM careers.',
    demoLink: '#',
    codeLink: '#'
  }, {
    title: 'Authentication Microservice',
    description: 'Built a secure, scalable authentication service with multi-factor authentication, SSO capabilities, and comprehensive audit logging.',
    image: 'https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    tags: ['Security', 'Backend', 'API'],
    category: 'Security',
    technologies: ['Django', 'OAuth2', 'JWT', 'PostgreSQL', 'Redis', 'Docker'],
    challenges: ['Implemented TOTP-based multi-factor authentication', 'Developed OAuth2 provider capabilities with role-based access control', 'Created comprehensive security logging and alerting', 'Built rate limiting and brute force protection'],
    results: 'Service handles authentication for 5+ applications with 100,000+ users, with zero security breaches since implementation.',
    demoLink: '#',
    codeLink: '#'
  }, {
    title: 'Real-time Analytics Dashboard',
    description: 'Created a real-time analytics platform for monitoring system performance, user behavior, and security events across distributed applications.',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    tags: ['Full Stack', 'Analytics', 'Real-time'],
    category: 'Full Stack',
    technologies: ['React', 'Node.js', 'Socket.io', 'Elasticsearch', 'Kibana', 'Kafka'],
    challenges: ['Processed and visualized high-volume event streams in real-time', 'Built customizable dashboards with drill-down capabilities', 'Implemented anomaly detection for security events', 'Created alert systems for critical metrics'],
    results: 'Dashboard has helped identify and resolve performance bottlenecks, reducing average response time by 40% and improving user experience.',
    demoLink: '#',
    codeLink: '#'
  }];
  const handleOpenModal = project => {
    setSelectedProject(project);
    onOpen();
  };
  const filteredProjects = filter === 'All' ? projects : projects.filter(project => project.category === filter);
  return <Box id="projects" py={20} bg={bgColor} width="100%" data-id="element-243">
      <Container maxW="1200px" data-id="element-244">
        <VStack spacing={12} data-id="element-245">
          <VStack spacing={4} textAlign="center" maxW="800px" data-id="element-246">
            <Heading as="h2" size="xl" data-id="element-247">
              Projects
            </Heading>
            <Divider width="100px" borderColor="blue.500" borderWidth={2} data-id="element-248" />
            <Text fontSize="lg" color={useColorModeValue('gray.600', 'gray.300')} data-id="element-249">
              Featured work highlighting security expertise and backend
              development
            </Text>
          </VStack>
          <Flex justifyContent="center" mb={8} data-id="element-250">
            <ButtonGroup variant="outline" spacing={4} flexWrap="wrap" data-id="element-251">
              {['All', 'Backend', 'Security', 'Full Stack'].map(category => <Button key={category} colorScheme={filter === category ? 'blue' : 'gray'} onClick={() => setFilter(category)} mb={{
              base: 2,
              md: 0
            }} data-id="element-252">
                  {category}
                </Button>)}
            </ButtonGroup>
          </Flex>
          <SimpleGrid columns={{
          base: 1,
          md: 2,
          lg: 3
        }} spacing={8} data-id="element-253">
            {filteredProjects.map((project, index) => <ProjectCard key={index} project={project} onClick={() => handleOpenModal(project)} data-id="element-254" />)}
          </SimpleGrid>
        </VStack>
      </Container>
      {selectedProject && <ProjectModal project={selectedProject} isOpen={isOpen} onClose={onClose} data-id="element-255" />}
    </Box>;
}